import React, { useEffect } from "react";
import { appEventEmitter } from "../../context/EventContext";
import { AppEvents } from "../../types/events";
import apiClient from '../../helpers/apiClient';
import { environment } from "../../environment";

const UserListener: React.FC = () => {

  useEffect(() => {
    /**
     * Send event to the API for usage tracking
     *
     * @param data
     */
    const sendEvent = async (data: any): Promise<void> => {
      try {
        console.log("Sending event:", data);
        await apiClient.post('/api/usage', data, {
          params: { app: environment.usage.app_name },
        });
      } catch (error) {
        console.error("Error sending event:", error);
      }
    };

    const onLogin = (data: AppEvents["login:user"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "login",
        resource: data.resource || "user",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        fired_by: data.user?.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onMicrosoftLogin = (data: AppEvents["login:microsoft"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "login",
        resource: data.resource || "microsoft",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        fired_by: data.user?.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onLogout = (data: AppEvents["logout:user"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "logout",
        resource: data.resource || "user",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "end",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        fired_by: data.user?.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onSignup = (data: AppEvents["signup:user"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "signup",
        resource: data.resource || "user",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        fired_by: data.user?.id,
        fired_at: new Date().toISOString(),
      });
    };

    /**
     * Handle login failed event to track failed login attempts
     *
     * @param data
     */
    const onLoginFailed = (data: AppEvents["login:failed"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 3, // 3 = Warning event
        action: data.action,
        resource: data.resource,
        agent: data.agent,
        event_phase: data.event_phase,
        session: data.user,
        fired_by: data.user,
        fired_at: new Date().toISOString(),
      });
    }

    const onDisconnect = (data: AppEvents["disconnect:user"]) => {
      console.log(`Disconnecting user: ${data.user.name} (${data.user.id}) and access token: ${data.user.access_token}`);

      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "disconnect",
        resource: data.resource || "user",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "end",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        fired_by: data.user?.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onCallReceive = (data: AppEvents["call:incoming"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "send",
        resource: data.resource || "call",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        caller: data.caller || "guest",
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onCallAccept = (data: AppEvents["call:accepted"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "accept",
        resource: data.resource || "call",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        caller: data.caller || "guest",
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onCallReject = (data: AppEvents["call:rejected"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "reject",
        resource: data.resource || "call",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "failed",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        caller: data.caller || "guest",
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onCallStarted = (data: AppEvents["call:started"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "start",
        resource: data.resource || "call",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        participants: data.participants,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onRoomCreated = (data: AppEvents["room:created"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "create",
        resource: data.resource || "room",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onRoomLeave = (data: AppEvents["room:left"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "leave",
        resource: data.resource || "room",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "end",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onRoomJoin = (data: AppEvents["room:joined"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "join",
        resource: data.resource || "room",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onRoomDestroyed = (data: AppEvents["room:destroyed"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "destroy",
        resource: data.resource || "room",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "destroy",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    };

    const onStreamVideoToggle = (data: AppEvents["stream:video:toggled"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "toggle",
        resource: data.resource || "stream",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        enabled: data.enabled,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onStreamAudioToggle = (data: AppEvents["stream:audio:toggled"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "toggle",
        resource: data.resource || "stream",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        enabled: data.enabled,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onStreamScreenStart = (data: AppEvents["stream:screen:started"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "share",
        resource: data.resource || "stream",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onStreamScreenEnd = (data: AppEvents["stream:screen:ended"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "unshare",
        resource: data.resource || "stream",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "end",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onMessageSend = (data: AppEvents["message:sent"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "send",
        resource: data.resource || "message",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "sent",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onMessageReceive = (data: AppEvents["message:received"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "receive",
        resource: data.resource || "message",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "received",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        sender: data.sender,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onFileShare = (data: AppEvents["file:shared"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "share",
        resource: data.resource || "file",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "shared",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        sent_to: data.sent_to,
        filename: data.filename,
        filesize: data.filesize,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onFileDownload = (data: AppEvents["file:downloaded"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "download",
        resource: data.resource || "file",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "received",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        filename: data.filename,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onFileReject = (data: AppEvents["file:rejected"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "reject",
        resource: data.resource || "file",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "rejected",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        room: data.room,
        filename: data.filename,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onDeviceStart = (data: AppEvents["device:started"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "start",
        resource: data.resource || "device",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        device: data.device,
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onDeviceStop = (data: AppEvents["device:stopped"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "stop",
        resource: data.resource || "device",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "end",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        device: data.device,
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onAppStart = (data: AppEvents["app:started"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "start",
        resource: data.resource || "app",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "start",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        app: data.app,
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onAppStop = (data: AppEvents["app:stopped"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 1, // 1 = General event
        action: data.action || "stop",
        resource: data.resource || "app",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "end",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        app: data.app,
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    const onAppFailed = (data: AppEvents["app:failed"]) => {
      sendEvent({
        app_id: environment.usage.app_id,
        event_type_id: 3, // 3 = Warning event
        action: data.action || "failed",
        resource: data.resource || "app",
        agent: data.agent || "guest",
        event_phase: data.event_phase || "failed",
        session: data.user?.access_token?.split('.')[1]?.slice(-10),
        app: data.app,
        room: data.room,
        fired_by: data.user.id,
        fired_at: new Date().toISOString(),
      });
    }

    appEventEmitter.on("login:user", onLogin);
    appEventEmitter.on("login:microsoft", onMicrosoftLogin);
    appEventEmitter.on("logout:user", onLogout);
    appEventEmitter.on("signup:user", onSignup);
    appEventEmitter.on("login:failed", onLoginFailed);
    appEventEmitter.on("disconnect:user", onDisconnect);

    appEventEmitter.on("call:incoming", onCallReceive);
    appEventEmitter.on("call:started", onCallStarted);
    appEventEmitter.on("call:accepted", onCallAccept);
    appEventEmitter.on("call:rejected", onCallReject);

    appEventEmitter.on("room:created", onRoomCreated);
    appEventEmitter.on("room:joined", onRoomJoin);
    appEventEmitter.on("room:left", onRoomLeave);
    appEventEmitter.on("room:destroyed", onRoomDestroyed);

    appEventEmitter.on("stream:video:toggled", onStreamVideoToggle);
    appEventEmitter.on("stream:audio:toggled", onStreamAudioToggle);
    appEventEmitter.on("stream:screen:started", onStreamScreenStart);
    appEventEmitter.on("stream:screen:ended", onStreamScreenEnd);

    appEventEmitter.on("message:sent", onMessageSend);
    appEventEmitter.on("message:received", onMessageReceive);

    appEventEmitter.on("file:shared", onFileShare);
    appEventEmitter.on("file:downloaded", onFileDownload);
    appEventEmitter.on("file:rejected", onFileReject);

    appEventEmitter.on("device:started", onDeviceStart);
    appEventEmitter.on("device:stopped", onDeviceStop);

    appEventEmitter.on("app:started", onAppStart);
    appEventEmitter.on("app:stopped", onAppStop);
    appEventEmitter.on("app:failed", onAppFailed);

    return () => {
      appEventEmitter.off("login:user", onLogin);
      appEventEmitter.off("login:microsoft", onMicrosoftLogin);
      appEventEmitter.off("logout:user", onLogout);
      appEventEmitter.off("signup:user", onSignup);
      appEventEmitter.off("login:failed", onLoginFailed);
      appEventEmitter.off("disconnect:user", onDisconnect);

      appEventEmitter.off("call:incoming", onCallReceive);
      appEventEmitter.off("call:started", onCallStarted);
      appEventEmitter.off("call:accepted", onCallAccept);
      appEventEmitter.off("call:rejected", onCallReject);

      appEventEmitter.off("room:created", onRoomCreated);
      appEventEmitter.off("room:joined", onRoomJoin);
      appEventEmitter.off("room:left", onRoomLeave);
      appEventEmitter.off("room:destroyed", onRoomDestroyed);

      appEventEmitter.off("stream:video:toggled", onStreamVideoToggle);
      appEventEmitter.off("stream:audio:toggled", onStreamAudioToggle);
      appEventEmitter.off("stream:screen:started", onStreamScreenStart);
      appEventEmitter.off("stream:screen:ended", onStreamScreenEnd);

      appEventEmitter.off("message:sent", onMessageSend);
      appEventEmitter.off("message:received", onMessageReceive);

      appEventEmitter.off("file:shared", onFileShare);
      appEventEmitter.off("file:downloaded", onFileDownload);
      appEventEmitter.off("file:rejected", onFileReject);

      appEventEmitter.off("device:started", onDeviceStart);
      appEventEmitter.off("device:stopped", onDeviceStop);

      appEventEmitter.off("app:started", onAppStart);
      appEventEmitter.off("app:stopped", onAppStop);
      appEventEmitter.off("app:failed", onAppFailed);
    };
  });

  return null;
};

export default UserListener;
